import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Building, UserCheck, ArrowRight, CheckCircle, Star, Activity, ShieldCheck, Briefcase, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useGlobalToast } from '@/contexts/ToastContext';
import { useFormValidation, validation } from '@/utils/validation';
import type { SignupForm } from '@/types';
import { FormInput } from '@/components/auth/FormInput';
import { PasswordStrength } from '@/components/auth/PasswordStrength';
import { ErrorSummary } from '@/components/auth/ErrorSummary';

const roleOptions = [
  { value: 'customer', label: 'Customer', description: 'Post jobs and hire professionals', icon: UserCheck },
  { value: 'provider', label: 'Service Provider', description: 'Offer services and find work', icon: Building },
];

export function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, isLoading } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  const roleFromState = location.state?.role;

  const { data, errors, touched, updateField, touchField, validateAll } =
    useFormValidation<SignupForm & { confirmPassword: string }>(
      { email: '', password: '', confirmPassword: '', firstName: '', lastName: '', role: roleFromState || '', phone: '', businessName: '', providerType: 'INDIVIDUAL' },
      {
        email: validation.email,
        password: validation.password,
        confirmPassword: () => null,
        firstName: (v) => validation.required(v, 'First name'),
        lastName: (v) => validation.required(v, 'Last name'),
        role: () => null,
        phone: (v) => v ? validation.phone(v) : null,
        providerType: () => null,
        businessName: () => null,
      }
    );

  const [customErrors, setCustomErrors] = useState<{ confirmPassword?: string; businessName?: string }>({});
  const [roleError, setRoleError] = useState('');

  const validatePasswordConfirmation = () => {
    if (data.password && !data.confirmPassword) {
      setCustomErrors(prev => ({ ...prev, confirmPassword: 'Confirm password is required' })); return false;
    }
    if (data.password && data.confirmPassword && data.password !== data.confirmPassword) {
      setCustomErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' })); return false;
    }
    setCustomErrors(prev => ({ ...prev, confirmPassword: undefined }));
    return true;
  };

  const validateBusinessName = () => {
    if (data.role === 'provider' && data.providerType === 'COMPANY' && !data.businessName) {
      setCustomErrors(prev => ({ ...prev, businessName: 'Business name is required' })); return false;
    }
    setCustomErrors(prev => ({ ...prev, businessName: undefined }));
    return true;
  };

  useEffect(() => {
    if (roleFromState && !data.role) {
      updateField('role', roleFromState);
    }
  }, [roleFromState]);

  useEffect(() => {
    if (data.confirmPassword && touched.confirmPassword) validatePasswordConfirmation();
  }, [data.password, data.confirmPassword, touched.confirmPassword]);

  const handleRoleSelect = (value: string) => {
    updateField('role', value);
    setRoleError('');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      setTermsError('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    setTermsError('');

    if (!roleFromState && !data.role) {
      setRoleError('Please select an account type');
    } else {
      setRoleError('');
    }

    const isValidAll = validateAll();
    const isValidPassword = validatePasswordConfirmation();
    const isValidBusiness = validateBusinessName();

    if ((!roleFromState && !data.role) || !isValidAll || !isValidPassword || !isValidBusiness) {
      error('Validation Error', 'Please fill in all required fields');
      return;
    }

    try {
      await signup({
        email: data.email, password: data.password,
        firstName: data.firstName, lastName: data.lastName,
        role: data.role as 'customer' | 'provider',
        phone: data.phone || undefined,
        businessName: data.businessName || undefined,
        providerType: data.providerType as 'INDIVIDUAL' | 'COMPANY',
      });
      success('Account Created!', `Verification link sent to ${data.email}.`);
    } catch (err: any) {
      error('Registration Failed', err.message || 'Unable to create account. Please try again.');
    }
  };

  const allErrors = { ...errors, ...customErrors };
  const hasErrors = Object.values(allErrors).some(e => e) && Object.values(touched).some(t => t);
  const isProvider = data.role === 'provider';

  return (
    <div className="min-h-screen bg-white flex">
      <a href="#signup-form" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg">
        Skip to signup form
      </a>

      {/* ── Left panel (desktop) ── */}
      <div className="hidden lg:flex lg:w-96 xl:w-[420px] bg-primary-600 flex-col p-10 relative overflow-hidden flex-shrink-0">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white rounded-full" />
          <div className="absolute -bottom-32 -left-16 w-96 h-96 bg-white rounded-full" />
        </div>
        <div className="relative z-10 flex items-center gap-2 mb-auto">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold text-sm tracking-wide">RapidFix</span>
        </div>
        <div className="relative z-10 mt-16 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-white leading-tight">Find trusted professionals in minutes</h1>
            <p className="text-primary-200 text-sm mt-3 leading-relaxed">Join thousands of customers and service providers on Ireland's fastest growing marketplace.</p>
          </div>
          <div className="space-y-3 pt-2">
            {[
              { icon: Star, text: '10,000+ active users' },
              { icon: ShieldCheck, text: 'Secure & verified profiles' },
              { icon: Activity, text: 'Real-time job opportunities' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-primary-100">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 mt-12 flex items-center gap-2 text-xs text-primary-200">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Secure & encrypted connection
        </div>
      </div>

      {/* ── Right panel (form) ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col px-5 sm:px-10 lg:px-14 xl:px-20 py-10">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-gray-900 font-bold text-sm">RapidFix</span>
          </div>

          <div className="w-full max-w-md">
            {/* Heading */}
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('/login')} className="text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:underline">
                  Sign in
                </button>
              </p>
            </div>

            <form id="signup-form" className="space-y-4" onSubmit={onSubmit} noValidate>
              {hasErrors && <ErrorSummary errors={allErrors} touched={touched} />}

              {/* Account Type */}
              {!roleFromState && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {roleOptions.map((opt) => (
                      <button
                        key={opt.value} type="button"
                        onClick={() => handleRoleSelect(opt.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRoleSelect(opt.value); } }}
                        className={`p-3 border-2 text-left rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${data.role === opt.value ? 'bg-primary-50 border-primary-500' : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        aria-pressed={data.role === opt.value}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <opt.icon className={`w-4 h-4 ${data.role === opt.value ? 'text-primary-600' : 'text-gray-400'}`} />
                          <span className={`text-sm font-semibold ${data.role === opt.value ? 'text-primary-700' : 'text-gray-700'}`}>{opt.label}</span>
                          {data.role === opt.value && <CheckCircle className="w-3.5 h-3.5 text-primary-600 ml-auto flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500">{opt.description}</p>
                      </button>
                    ))}
                  </div>
                  {roleError && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />{roleError}
                    </p>
                  )}
                </div>
              )}

              {/* Provider Type */}
              {isProvider && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ value: 'INDIVIDUAL', label: 'Individual', sub: 'Sole trader / freelancer', Icon: User },
                    { value: 'COMPANY', label: 'Company', sub: 'Registered business', Icon: Building }]
                      .map(({ value, label, sub, Icon }) => (
                        <button key={value} type="button"
                          onClick={() => { updateField('providerType', value); touchField('providerType'); }}
                          className={`p-3 border-2 text-left rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${data.providerType === value ? 'bg-primary-50 border-primary-500' : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className={`w-4 h-4 ${data.providerType === value ? 'text-primary-600' : 'text-gray-400'}`} />
                            <span className={`text-sm font-semibold ${data.providerType === value ? 'text-primary-700' : 'text-gray-700'}`}>{label}</span>
                          </div>
                          <p className="text-xs text-gray-500">{sub}</p>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Business Name */}
              {data.role === 'provider' && data.providerType === 'COMPANY' && (
                <FormInput label="Business Name" value={data.businessName || ''} onChange={(v: string) => updateField('businessName', v)} onBlur={() => touchField('businessName')} placeholder="Your business name" icon={Building} error={customErrors.businessName} required showSuccess={!!data.businessName && !customErrors.businessName} />
              )}

              {/* Name */}
              <div className="grid grid-cols-2 gap-3">
                <FormInput label="First Name" value={data.firstName || ''} onChange={(v: string) => updateField('firstName', v)} onBlur={() => touchField('firstName')} placeholder="John" icon={User} error={touched.firstName ? errors.firstName : ''} required showSuccess={!!data.firstName && !errors.firstName} />
                <FormInput label="Last Name" value={data.lastName || ''} onChange={(v: string) => updateField('lastName', v)} onBlur={() => touchField('lastName')} placeholder="Doe" icon={User} error={touched.lastName ? errors.lastName : ''} required showSuccess={!!data.lastName && !errors.lastName} />
              </div>

              {/* Email */}
              <FormInput label="Email Address" value={data.email || ''} onChange={(v: string) => updateField('email', v)} onBlur={() => touchField('email')} placeholder="you@example.com" icon={Mail} type="email" error={touched.email ? errors.email : ''} required showSuccess={!!data.email && !errors.email} helperText="We'll send a verification link here" />

              {/* Phone */}
              <FormInput label="Phone Number" value={data.phone || ''} onChange={(v: string) => updateField('phone', v)} onBlur={() => touchField('phone')} placeholder="+353 or 08X XXX XXXX" icon={Activity} type="tel" error={touched.phone ? errors.phone : ''} showSuccess={!!data.phone && !errors.phone} helperText="Optional · Irish format" />

              {/* Password */}
              <div>
                <FormInput label="Password" value={data.password} onChange={(v: string) => { updateField('password', v); if (touched.password) touchField('password'); }} onBlur={() => touchField('password')} placeholder="Create a strong password" icon={Lock} error={touched.password ? errors.password : ''} required isPassword ariaLabel="Password" />
                {data.password && <div className="mt-2"><PasswordStrength password={data.password} /></div>}
              </div>

              {/* Confirm Password */}
              <FormInput label="Confirm Password" value={data.confirmPassword} onChange={(v: string) => { updateField('confirmPassword', v); if (v) touchField('confirmPassword'); }} onBlur={() => { touchField('confirmPassword'); validatePasswordConfirmation(); }} placeholder="Re-enter your password" icon={ShieldCheck} error={touched.confirmPassword ? customErrors.confirmPassword : ''} required isPassword showSuccess={!!data.confirmPassword && !customErrors.confirmPassword && data.password === data.confirmPassword} ariaLabel="Confirm password" />

              {/* Terms */}
              <div className={`flex items-start gap-3 p-3 border rounded-lg ${termsError ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
                <input type="checkbox" checked={termsAccepted} onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(''); }} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" aria-describedby="terms-error" />
                <div className="text-sm text-gray-600 leading-snug">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary-600 hover:underline font-medium">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="text-primary-600 hover:underline font-medium">Privacy Policy</Link>
                  {termsError && (
                    <p id="terms-error" className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />{termsError}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={isLoading} className="w-full py-2.5 bg-primary-600 text-white font-semibold text-sm rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-500 focus:ring-offset-2 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />Creating Account...</>
                ) : (
                  <>Create Account<ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
